<template>
  <div>
    <p v-if="documents.length === 0" class="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
      {{ t("no_document") }}
    </p>

    <div v-else class="space-y-4">
      <figure
        v-for="(document, index) in documents"
        :key="`${index}-${document.link}`"
        class="overflow-hidden rounded-md border border-gray-200"
      >
        <figcaption class="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-3 py-2">
          <span class="truncate text-xs font-medium text-gray-700">{{ document.name }}</span>
          <a
            :href="document.link"
            target="_blank"
            rel="noopener"
            class="inline-flex items-center gap-1 text-xs text-gray-500 underline hover:text-gray-700"
          >
            <ExternalLinkIcon class="h-4 w-4" aria-hidden="true" />
            {{ t("open_file") }}
          </a>
        </figcaption>

        <!-- Hauteurs en inline style : Tailwind 2 sans JIT ne gère pas les valeurs arbitraires en vh. -->
        <img
          v-if="previewKind(document) === 'image'"
          :src="document.link"
          :alt="document.name"
          class="w-full bg-gray-50 object-contain"
          :style="{ maxHeight: PREVIEW_HEIGHT }"
          loading="lazy"
        />
        <iframe
          v-else-if="previewKind(document) === 'pdf'"
          :src="pdfViewerUrl(document.link)"
          :title="document.name"
          class="w-full bg-gray-50"
          :style="{ height: PREVIEW_HEIGHT }"
          loading="lazy"
          frameborder="0"
        ></iframe>
        <p v-else class="px-3 py-4 text-sm text-gray-500">{{ t("preview_unavailable") }}</p>
      </figure>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { ExternalLinkIcon } from "@heroicons/vue/outline";

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "tiff", "tif", "bmp"];

// Hauteur d'un fichier affiché : l'essentiel de la fenêtre, en gardant les onglets visibles.
const PREVIEW_HEIGHT = "78vh";

export default {
  name: "AiAnalysisDocumentPreview",

  components: { ExternalLinkIcon },

  setup() {
    const { t } = useI18n();
    return { t, PREVIEW_HEIGHT };
  },

  props: {
    documents: {
      type: Array,
      default: () => [],
    },
  },

  methods: {
    // Masque le panneau de vignettes du lecteur PDF et ajuste la page à la largeur de la colonne.
    pdfViewerUrl(link) {
      return `${link}#navpanes=0&view=FitH`;
    },

    // Type d'aperçu déduit de l'extension : le nom du fichier d'abord, l'URL en repli.
    previewKind(document) {
      const extension = extensionOf(document?.name) || extensionOf(urlPath(document?.link));
      if (IMAGE_EXTENSIONS.includes(extension)) return "image";
      if (extension === "pdf") return "pdf";
      return null;
    },
  },
};

function extensionOf(value) {
  const match = /\.([a-z0-9]+)$/i.exec(value || "");
  return match ? match[1].toLowerCase() : null;
}

// Les liens ActiveStorage portent une query string signée : on ne garde que le chemin.
function urlPath(link) {
  return (link || "").split(/[?#]/)[0];
}
</script>

<i18n src="./AiAnalysisDocumentPreview.i18n.json"></i18n>
